import React from 'react';

import { PlayerMode } from '$/components/WebinarPlayer/types';
import { WebinarPlayerUIState } from '$/components/WebinarPlayer/context';
import { PublicEWebinarFragment } from '$/GraphQL/schema/schema';
import EDate from '$/shared/frontend/utils/EDate';
import { logger } from '$/utils/logger';
import { LocalTimeZone } from '$/utils/LocalTimeZone';
import { useServerContext } from '$/hooks/useServerDateNow';

interface InactiveReplayProps {
  inactive: boolean;
  rejoin: () => void;
  ewebinar: PublicEWebinarFragment;
}

export const useInactiveReplay = (
  contextData: Pick<
    WebinarPlayerUIState,
    | 'lastActiveDateRef'
    | 'isPlaying'
    | 'mode'
    | 'isReplayExpired'
    | 'inactiveReplay'
    | 'onRejoinReplay'
    | 'onInactiveReplay'
    | 'ewebinar'
    | 'pauseTimer'
  >
): InactiveReplayProps => {
  const {
    lastActiveDateRef,
    isPlaying,
    mode,
    isReplayExpired,
    inactiveReplay,
    onRejoinReplay,
    onInactiveReplay,
    ewebinar,
    pauseTimer,
  } = contextData;
  const { serverDateNow } = useServerContext();

  const INACTIVE_IN_MINUTES = 10;

  React.useEffect(() => {
    if (inactiveReplay || isPlaying || mode !== PlayerMode.replay || isReplayExpired) return;

    if (!isPlaying && pauseTimer > 0) {
      logger.debug('PLAY', 'pauseTimer > 0 -> SKIP Inactive Replay', isPlaying, pauseTimer);
      return;
    }

    const interval = setInterval(() => {
      // Check every minute if we've been inactive
      const expireAt = lastActiveDateRef.current.addMinutes(INACTIVE_IN_MINUTES);
      if (expireAt.isSameOrBefore(new EDate(serverDateNow(), LocalTimeZone.name))) {
        logger.debug('ANIM', 'Replay inactive ', {
          expireAt: expireAt.toISOString(),
          now: new EDate(serverDateNow(), LocalTimeZone.name).toISOString(),
          isSameOrBefore: expireAt.isSameOrBefore(new EDate(serverDateNow(), LocalTimeZone.name)),
        });

        onInactiveReplay();
      }
    }, 60 * 1000); // 1 min

    return (): void => {
      interval && clearInterval(interval);
    };
  }, [
    isPlaying,
    mode,
    lastActiveDateRef,
    isReplayExpired,
    inactiveReplay,
    onInactiveReplay,
    pauseTimer,
    serverDateNow,
  ]);

  return { ewebinar, rejoin: onRejoinReplay, inactive: inactiveReplay };
};
